// Brief descriptions of common lab tests
export const testDescriptions: Record<string, string> = {
  // Blood Count Tests
  'MCV': 'Measures the average size of your red blood cells to help detect anemia types',
  'MCH': 'Shows how much oxygen-carrying hemoglobin is in each red blood cell',
  'MCHC': 'Indicates the concentration of hemoglobin in your red blood cells',
  'RBC': 'Counts your red blood cells which carry oxygen throughout your body',
  'WBC': 'Measures infection-fighting white blood cells to check immune system health',
  'PDW': 'Shows if your blood-clotting platelets vary much in size',
  'RDW': 'Checks if your red blood cells are uniform in size or vary significantly',
  'MPV': 'Measures average platelet size to help assess blood clotting function',
  'HGB': 'Measures hemoglobin, the protein that carries oxygen in your blood',
  'HCT': 'Shows what percentage of your blood is made up of red blood cells',
  'PLT': 'Counts platelets that help your blood clot when you have an injury',
  'NEUT': 'Measures neutrophils, your main defense against bacterial infections',
  'LYMPH': 'Counts lymphocytes that fight viruses and produce antibodies',
  'MONO': 'Measures monocytes that help clean up damaged cells and fight infections',
  'EOS': 'Counts eosinophils which increase with allergies or parasitic infections',
  'BASO': 'Measures basophils involved in allergic reactions and inflammation',
  
  // Metabolic Panel
  'GLUCOSE': 'Measures blood sugar levels to screen for diabetes and monitor control',
  'BUN': 'Checks how well your kidneys filter waste from your blood',
  'CREATININE': 'Measures kidney function by checking waste product clearance',
  'EGFR': 'Estimates how well your kidneys filter waste from your blood',
  'SODIUM': 'Essential electrolyte that regulates water balance and nerve function',
  'POTASSIUM': 'Critical electrolyte for heart rhythm and muscle contractions',
  'CHLORIDE': 'Electrolyte that helps maintain proper blood volume and pressure',
  'CO2': 'Measures blood acid-base balance and breathing effectiveness',
  'CALCIUM': 'Essential mineral for strong bones, muscle function, and nerve signals',
  'ALBUMIN': 'Main blood protein that indicates liver function and nutrition status',
  'TOTAL PROTEIN': 'Measures all proteins to assess liver, kidney, and nutritional health',
  'ALT': 'Liver enzyme that increases when liver cells are damaged',
  'AST': 'Enzyme found in liver and heart that rises with tissue damage',
  'ALP': 'Enzyme that can indicate liver, bile duct, or bone problems',
  'BILIRUBIN': 'Breakdown product of red blood cells processed by the liver',
  
  // Lipid Panel
  'CHOLESTEROL': 'Total amount of cholesterol in your blood affecting heart disease risk',
  'TRIGLYCERIDES': 'Type of fat in blood that increases with sugar and alcohol intake',
  'HDL': 'Good cholesterol that removes bad cholesterol from arteries',
  'LDL': 'Bad cholesterol that can build up in arteries and cause blockages',
  'VLDL': 'Cholesterol type that carries triglycerides and contributes to plaque buildup',
  
  // Thyroid
  'TSH': 'Controls thyroid function - high levels may indicate underactive thyroid',
  'T3': 'Active thyroid hormone that regulates metabolism and energy',
  'T4': 'Main thyroid hormone that converts to T3 for metabolism regulation',
  'FREE T3': 'Active thyroid hormone not bound to proteins, available for use',
  'FREE T4': 'Available thyroid hormone that indicates true thyroid function',
  
  // Hormones
  'TESTOSTERONE': 'Primary male hormone affecting muscle mass, bone density, and libido',
  'ESTRADIOL': 'Main female estrogen hormone regulating reproductive system and bone health',
  'PROGESTERONE': 'Hormone that prepares uterus for pregnancy and regulates menstrual cycle',
  'CORTISOL': 'Stress hormone that regulates blood sugar, inflammation, and metabolism',
  'DHEA': 'Hormone building block that converts to testosterone and estrogen',
  'FSH': 'Stimulates egg development in women and sperm production in men',
  'LH': 'Triggers ovulation in women and testosterone production in men',
  'PROLACTIN': 'Hormone that stimulates breast milk production and affects fertility',
  
  // Vitamins & Minerals
  'VITAMIN D': 'Essential for calcium absorption, bone strength, and immune function',
  'VITAMIN B12': 'Crucial for nerve health, DNA production, and preventing anemia',
  'FOLATE': 'B vitamin essential for cell division and preventing birth defects',
  'FERRITIN': 'Shows how much iron your body has stored for making red blood cells',
  'IRON': 'Essential mineral that helps red blood cells carry oxygen',
  'MAGNESIUM': 'Mineral vital for muscle contractions, heart rhythm, and bone health',
  'PHOSPHORUS': 'Works with calcium to build strong bones and teeth',
  
  // Inflammatory Markers
  'CRP': 'Protein that rises quickly with inflammation or infection in the body',
  'ESR': 'Measures how fast red blood cells settle, indicating inflammation',
  'HOMOCYSTEINE': 'Amino acid that at high levels may increase heart disease risk',
  
  // Cancer Markers
  'PSA': 'Protein made by prostate that screens for prostate problems including cancer',
  'CEA': 'Marker that may be elevated in certain cancers, especially colon cancer',
  'CA125': 'Protein that may be elevated in ovarian cancer and other conditions',
  'CA19-9': 'Marker often elevated in pancreatic and other digestive cancers',
  'AFP': 'Protein that may indicate liver cancer or other liver conditions',
  
  // Other Common Tests
  'HBA1C': 'Shows average blood sugar over past 2-3 months for diabetes monitoring',
  'INR': 'Measures blood clotting time for those on blood thinners like warfarin',
  'PT': 'Tests how long blood takes to clot, checking clotting factors',
  'PTT': 'Evaluates blood clotting pathway to detect bleeding disorders',
  'URIC ACID': 'Waste product that can form crystals causing gout when too high',
  'AMYLASE': 'Digestive enzyme that breaks down starches, elevated in pancreas problems',
  'LIPASE': 'Enzyme that digests fats, rises with pancreatic inflammation',
  'GGT': 'Liver enzyme sensitive to alcohol use and bile duct problems',
  'LDH': 'Enzyme released when cells are damaged, indicating tissue injury',
  'CK': 'Enzyme that increases with muscle damage from injury or disease',
  'TROPONIN': 'Protein released when heart muscle is damaged, key heart attack marker',
  
  // Estonian test names
  'KOLESTEROOL': 'Total amount of cholesterol in your blood affecting heart disease risk',
  'TRIGLÜTSERIIDID': 'Type of fat in blood that increases with sugar and alcohol intake',
  'ÖSTRADIOOL': 'Main female estrogen hormone regulating reproductive system and bone health',
  'TESTOSTEROON': 'Primary male hormone affecting muscle mass, bone density, and libido',
  'VABA TESTOSTEROON': 'Free testosterone not bound to proteins, available for body use',
  'HEMOGLOBIIN': 'Measures hemoglobin, the protein that carries oxygen in your blood',
  
  // Common variations
  'HEMOGLOBIN': 'Measures hemoglobin, the protein that carries oxygen in your blood',
  'HAEMOGLOBIN': 'Measures hemoglobin, the protein that carries oxygen in your blood',
  'HB': 'Measures hemoglobin, the protein that carries oxygen in your blood',
  'DHEAS': 'Hormone building block that converts to testosterone and estrogen',
  'SHBG': 'Protein that binds sex hormones, affecting their availability in the body',
  'FT3': 'Active thyroid hormone not bound to proteins, available for use',
  'FT4': 'Available thyroid hormone that indicates true thyroid function',
  'BIOAV. TESTO.': 'Testosterone available for body use, not bound to proteins',
  'BIOAVAILABLE TESTOSTERONE': 'Testosterone available for body use, not bound to proteins',
  'FAI': 'Free Androgen Index - ratio showing available testosterone levels',
  'FREE ANDROGEN INDEX': 'Ratio showing available testosterone levels',
  'ALAT': 'Liver enzyme that increases when liver cells are damaged',
  'ASAT': 'Enzyme found in liver and heart that rises with tissue damage',
  'PCT': 'Plateletcrit - percentage of blood volume occupied by platelets',
  'NEU': 'Measures neutrophils, your main defense against bacterial infections',
  'LYM': 'Counts lymphocytes that fight viruses and produce antibodies',
  'MON': 'Measures monocytes that help clean up damaged cells and fight infections',
  'OESTRADIOL': 'Main female estrogen hormone regulating reproductive system and bone health'
};

// Helper function to get description (case-insensitive)
export function getTestDescription(testName: string): string {
  const upperName = testName.toUpperCase();
  return testDescriptions[upperName] || 'Lab test measurement';
}